import { cast, Instance, SnapshotIn, types } from 'mobx-state-tree'
import { SelectorOption } from '../SelectorOption'
import { DateTime } from 'luxon'

export const SubscriptionProduct = types
  .model('SubscriptionProduct')
  .props({
    id: types.optional(types.identifierNumber, 0),
    title: types.optional(types.string, ''),
    description: types.maybeNull(types.string),
    sku: types.optional(types.string, ''),
    duration_days: types.optional(types.number, 365),
    is_active: types.optional(types.boolean, false),
    color: types.maybeNull(types.string),
    change_applies_to: types.optional(types.string, 'new'),
    tag_ids: types.maybeNull(types.array(types.frozen())),
    content: types.optional(types.array(SelectorOption), []),
    touched: types.optional(types.map(types.boolean), {}),
    created_at: types.optional(types.string, ''),
  })
  .volatile(() => ({
    existingSkus: [] as string[],
  }))
  .views((self) => ({
    get createdDate() {
      return self.created_at ? DateTime.fromISO(self.created_at).toFormat('LLL dd, yyyy') : '—'
    },
    get selectedContent() {
      return self.content.filter((c) => c.checked)
    },
    get formContentDisplayLabel() {
      return `Content (${[
        this.selectedPaths.length > 0 &&
          `${this.selectedPaths.length} path${this.selectedPaths.length === 1 ? '' : 's'}`,
        this.selectedTopics.length > 0 &&
          `${this.selectedTopics.length} topic${this.selectedTopics.length === 1 ? '' : 's'}`,
        this.selectedQuizzes.length > 0 &&
          `${this.selectedQuizzes.length} quiz${this.selectedQuizzes.length === 1 ? '' : 'zes'}`,
      ]
        .filter(Boolean)
        .join(', ')})`
    },
    get selectedTopics() {
      return self.content.filter((c) => c.checked && c.type === 'topics')
    },
    get selectedQuizzes() {
      return self.content.filter((c) => c.checked && c.type === 'quizzes')
    },
    get selectedPaths() {
      return self.content.filter((c) => c.checked && c.type === 'learning paths')
    },
    get durationText() {
      return `${self.duration_days} days`
    },
    get errors() {
      const errs: Record<string, string> = {}
      if (!self.title.trim()) {
        errs.title = 'Title is required'
      }
      if (!self.sku.trim() && self.is_active) {
        errs.sku = 'SKU is required'
      } else if (self.existingSkus.includes(self.sku.trim())) {
        errs.sku = 'This SKU is already in use'
      }
      if (self.duration_days <= 0) {
        errs.duration_days = 'Duration must be greater than 0'
      }
      if (self.duration_days > 365) {
        errs.duration_days = 'Duration must be less than or equal to 365'
      }
      if (self.color && !/^#[0-9A-Fa-f]{6}$/.test(self.color)) {
        errs.color = 'A valid hex color is required'
      }
      return errs
    },
    get payload() {
      const typeMap: Record<string, string> = {
        topics: 'topic',
        quizzes: 'quiz',
        'learning paths': 'path',
      }
      const selectedContent = self.content.filter((c) => c.checked)
      return {
        title: self.title,
        description: self.description || null,
        sku: self.sku,
        duration_days: self.duration_days,
        is_active: self.is_active,
        color: self.color || null,
        change_applies_to: self.change_applies_to,
        tag_ids:
          self.tag_ids && self.tag_ids.length > 0 ? self.tag_ids.map((t: any) => t.id ?? t) : [],
        content: selectedContent.map((c) => ({
          id: String(c.id),
          type: typeMap[c.type ?? ''] ?? c.type,
        })),
      }
    },
  }))
  .actions((self) => ({
    set<K extends keyof SnapshotIn<typeof self>, T extends SnapshotIn<typeof self>>(
      key: K,
      value: T[K],
    ) {
      // @ts-ignore
      self[key] = cast(value)
    },
    touch(key: string) {
      self.touched.set(key, true)
    },
    setExistingSkus(skus: string[]) {
      self.existingSkus = skus
    },
  }))

export type SubscriptionProductType = Instance<typeof SubscriptionProduct>
